// Quick keys: one-tap buttons over the grid and the scan zone for what every
// counter sells without a tag (carry bag, gift wrap) and for the floor services.
// Product keys find their item by its code, else by a name pattern, among the
// synced products.
//
// The store's set comes from its POS config (cwr_pos_config.config_json), as one
// entry whose value is an object or JSON text:
//   "fashionQuickKeys": {
//     "keys": [{ "id": "carry-bag", "kind": "product", "label": "Carry bag", "code": "FSH-CB-M" },
//              { "id": "alteration", "kind": "panel", "label": "Alteration", "panel": "alterations" }],
//     "carryBagCode": "FSH-CB-M", "giftWrapCode": "FSH-GW-01", "tillCanEdit": "Y" }
// Every part is optional: no "keys" keeps the default four, the two codes fill
// the carry bag and gift wrap keys, and "tillCanEdit": "N" stops a counter
// changing its keys. Where it may, a counter's own set (localStorage) wins.

export const QUICK_KEYS_KEY = "cw.fashion.quickKeys";
export const MAX_KEYS = 8;

// kind "product" adds an item; kind "panel" opens a till screen; "stores" opens
// other stores' stock for the style on the selected line.
export const DEFAULT_KEYS = Object.freeze([
  { id: "carry-bag", kind: "product", label: "Carry bag", code: "", match: "carry ?bag|paper ?bag|shopping ?bag" },
  { id: "gift-wrap", kind: "product", label: "Gift wrap", code: "", match: "gift ?wrap|gift ?box" },
  { id: "alteration", kind: "panel", label: "Alteration", panel: "alterations" },
  { id: "stores", kind: "stores", label: "Other stores" },
]);

const clean = (key) => {
  if (!key || typeof key !== "object" || !key.id || !key.label) return null;
  const kind = ["product", "panel", "stores"].includes(key.kind) ? key.kind : "product";
  return {
    id: String(key.id),
    kind,
    label: String(key.label).slice(0, 24),
    code: kind === "product" ? String(key.code || "").trim() : "",
    match: kind === "product" ? String(key.match || "") : "",
    panel: kind === "panel" ? String(key.panel || "") : "",
    hidden: key.hidden === true,
  };
};

export const CONFIG_ENTRY = "fashionQuickKeys";

const readEntry = (posConfig) => {
  const raw = posConfig?.[CONFIG_ENTRY];
  if (raw && typeof raw === "object") return raw;
  if (typeof raw !== "string" || !raw.trim().startsWith("{")) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

// { keys, canEdit }: the store's keys from its POS config, else the defaults.
export function configQuickKeys(posConfig) {
  const entry = readEntry(posConfig);
  const listed = Array.isArray(entry.keys) ? entry.keys.map(clean).filter(Boolean).slice(0, MAX_KEYS) : [];
  const keys = listed.length ? listed : DEFAULT_KEYS.map(clean);
  const codes = { "carry-bag": String(entry.carryBagCode || "").trim(), "gift-wrap": String(entry.giftWrapCode || "").trim() };
  return {
    keys: keys.map((k) => (k.kind === "product" && !k.code && codes[k.id] ? { ...k, code: codes[k.id] } : k)),
    canEdit: String(entry.tillCanEdit ?? "Y").trim().toUpperCase() !== "N",
  };
}

export function loadQuickKeys(storage = globalThis.localStorage, posConfig = null) {
  const store = configQuickKeys(posConfig);
  if (!store.canEdit) return store.keys;
  try {
    const raw = storage?.getItem(QUICK_KEYS_KEY);
    if (!raw) return store.keys;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return store.keys;
    const keys = list.map(clean).filter(Boolean).slice(0, MAX_KEYS);
    return keys.length ? keys : store.keys;
  } catch {
    return store.keys;
  }
}

export function saveQuickKeys(keys, storage = globalThis.localStorage) {
  const list = (keys || []).map(clean).filter(Boolean).slice(0, MAX_KEYS);
  try {
    storage?.setItem(QUICK_KEYS_KEY, JSON.stringify(list));
  } catch {
    // per-device convenience only; the defaults still work
  }
  return list;
}

// Back to the store's set from its POS config.
export function resetQuickKeys(storage = globalThis.localStorage, posConfig = null) {
  try {
    storage?.removeItem(QUICK_KEYS_KEY);
  } catch {
    // nothing saved to clear
  }
  return configQuickKeys(posConfig).keys;
}

const lc = (s) => String(s || "").toLowerCase();

// The product a product key adds: an exact item code / barcode first, else the
// first product whose name fits the key's pattern. null when the till has none.
export function resolveQuickKey(key, products) {
  if (!key || key.kind !== "product") return null;
  const list = products || [];
  const code = lc(key.code).trim();
  if (code) {
    const hit = list.find((p) => lc(p?.value) === code || lc(p?.upc) === code || (Array.isArray(p?.upcIndex) && p.upcIndex.some((u) => lc(u) === code)));
    if (hit) return hit;
  }
  if (!key.match) return null;
  let re;
  try {
    re = new RegExp(key.match, "i");
  } catch {
    return null;
  }
  return list.find((p) => p?.isactive !== "N" && re.test(p?.name || "")) || null;
}

export function newKeyId(keys) {
  let n = 1;
  const ids = new Set((keys || []).map((k) => k.id));
  while (ids.has(`key-${n}`)) n += 1;
  return `key-${n}`;
}
